import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { debounce } from '../utils/helpers'

// Create the search context
const SearchContext = createContext()

// Custom hook to use the search context
export const useSearchContext = () => {
  const context = useContext(SearchContext)
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider')
  }
  return context
}

// Search provider component
export const SearchProvider = ({ children }) => {
  // Shared search state
  const [searchTerm, setSearchTerm] = useState('')
  const [recentSearches, setRecentSearches] = useState([])
  const [searchSuggestions, setSuggestions] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  // Filter state that might be shared across components
  const [activeFilters, setActiveFilters] = useState({})
  const [filterCounts, setFilterCounts] = useState({})

  // UI state
  const [searchFocused, setSearchFocused] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Memoized search history actions
  const addToRecentSearches = useCallback(
    (searchData) => {
      setRecentSearches((prev) => {
        const filtered = prev.filter(
          (search) =>
            search.term !== searchData.term || JSON.stringify(search.filters) !== JSON.stringify(searchData.filters)
        )
        return [searchData, ...filtered].slice(0, 10) // Keep last 10 searches
      })

      // Store in localStorage for persistence
      localStorage.setItem('recentSearches', JSON.stringify([searchData, ...recentSearches].slice(0, 10)))
    },
    [recentSearches]
  )

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([])
    localStorage.removeItem('recentSearches')
  }, [])

  // Debounced search suggestions
  const debouncedGetSuggestions = useMemo(
    () =>
      debounce(async (term) => {
        if (term.length < 2) {
          setSuggestions([])
          return
        }

        setIsSearching(true)
        try {
          // Mock suggestions - replace with actual API call
          const mockSuggestions = [
            `${term} in Cape Town`,
            `${term} in Johannesburg`,
            `${term} in Durban`,
            `Dr. ${term}`,
            `${term} clinic`
          ].filter((suggestion) => suggestion.toLowerCase().includes(term.toLowerCase()))

          setSuggestions(mockSuggestions.slice(0, 5))
        } catch (error) {
          console.error('Error fetching suggestions:', error)
          setSuggestions([])
        } finally {
          setIsSearching(false)
        }
      }, 300),
    []
  )

  // Handle search term changes
  const handleSearchTermChange = useCallback(
    (term) => {
      setSearchTerm(term)
      debouncedGetSuggestions(term)
      setShowSuggestions(term.length > 0 && searchFocused)
    },
    [debouncedGetSuggestions, searchFocused]
  )

  // Filter management
  const updateActiveFilters = useCallback((filterType, value, action = 'toggle') => {
    setActiveFilters((prev) => {
      const updated = { ...prev }

      if (action === 'set') {
        updated[filterType] = value
      } else if (action === 'toggle') {
        if (!updated[filterType]) {
          updated[filterType] = []
        }

        if (Array.isArray(updated[filterType])) {
          const index = updated[filterType].indexOf(value)
          if (index > -1) {
            updated[filterType] = updated[filterType].filter((item) => item !== value)
          } else {
            updated[filterType] = [...updated[filterType], value]
          }
        } else {
          updated[filterType] = updated[filterType] === value ? null : value
        }
      } else if (action === 'remove') {
        if (Array.isArray(updated[filterType])) {
          updated[filterType] = updated[filterType].filter((item) => item !== value)
        } else {
          delete updated[filterType]
        }
      }

      return updated
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setActiveFilters({})
  }, [])

  const getActiveFilterCount = useCallback(() => {
    return Object.values(activeFilters).reduce((count, value) => {
      if (Array.isArray(value)) {
        return count + value.length
      }
      return value ? count + 1 : count
    }, 0)
  }, [activeFilters])

  // Search focus management
  const handleSearchFocus = useCallback(() => {
    setSearchFocused(true)
    setShowSuggestions(searchTerm.length > 0)
  }, [searchTerm])

  const handleSearchBlur = useCallback(() => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setSearchFocused(false)
      setShowSuggestions(false)
    }, 200)
  }, [])

  // Load recent searches from localStorage on mount
  React.useEffect(() => {
    const saved = localStorage.getItem('recentSearches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (error) {
        console.error('Error loading recent searches:', error)
      }
    }
  }, [])

  // Context value
  const contextValue = useMemo(
    () => ({
      // Search state
      searchTerm,
      recentSearches,
      searchSuggestions,
      isSearching,
      searchFocused,
      showSuggestions,

      // Filter state
      activeFilters,
      filterCounts,

      // Search actions
      handleSearchTermChange,
      addToRecentSearches,
      clearRecentSearches,
      handleSearchFocus,
      handleSearchBlur,

      // Filter actions
      updateActiveFilters,
      clearAllFilters,
      getActiveFilterCount,

      // Setters (for advanced use cases)
      setSearchTerm,
      setSuggestions,
      setFilterCounts
    }),
    [
      searchTerm,
      recentSearches,
      searchSuggestions,
      isSearching,
      searchFocused,
      showSuggestions,
      activeFilters,
      filterCounts,
      handleSearchTermChange,
      addToRecentSearches,
      clearRecentSearches,
      handleSearchFocus,
      handleSearchBlur,
      updateActiveFilters,
      clearAllFilters,
      getActiveFilterCount
    ]
  )

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>
}

// Higher-order component for easy integration
export const withSearchContext = (Component) => {
  return function WrappedComponent(props) {
    return (
      <SearchProvider>
        <Component {...props} />
      </SearchProvider>
    )
  }
}

export default SearchContext
